import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebaseConfig';
import { updatePassword, updateEmail, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { collection, doc, addDoc, getDocs, deleteDoc, updateDoc } from 'firebase/firestore';
import { Typography, TextField, Button, Paper, Box, Alert, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import ConfirmationDialog from '../../components/confirmationDialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link } from 'react-router-dom';



const ProfilePage = () => {
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [children, setChildren] = useState([]);
  const [childName, setChildName] = useState('');
  const [childFirstName, setChildFirstName] = useState('');
  const [childClass, setChildClass] = useState(''); // Nouvel état pour la classe
  const [message, setMessage] = useState('');
  const [messageMdp, setMessageMdp] = useState('');
  const [error, setError] = useState('');
  const [errorMdp, setErrorMdp] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedChild, setSelectedChild] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [messageEmail, setMessageEmail] = useState('');
  const [errorEmail, setErrorEmail] = useState('');

  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (user) {
        setEmail(user.email);
        const childrenRef = collection(db, 'users', user.uid, 'children');
        const childrenSnap = await getDocs(childrenRef);
        const childrenList = childrenSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setChildren(childrenList);
      }
    };

    fetchData();
  }, []);

  const handleChangePassword = async () => {
    setMessageMdp('');
    setErrorMdp('');
    if (newPassword !== confirmNewPassword) {
      setErrorMdp('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }

    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setMessageMdp('Mot de passe mis à jour avec succès.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setErrorMdp('Erreur lors de la mise à jour du mot de passe. Veuillez vérifier vos informations.');
      console.error('Erreur lors de la mise à jour du mot de passe :', err);
    }
  };

  const handleChangeEmail = async () => {
    setMessageEmail('');
    setErrorEmail('');

    if (!newEmail.trim()) {
      setErrorEmail('Veuillez saisir une nouvelle adresse email.');
      return;
    }

    if (!emailPassword) {
      setErrorEmail('Veuillez saisir votre mot de passe actuel.');
      return;
    }

    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, emailPassword);
      await reauthenticateWithCredential(user, credential);
      await updateEmail(user, newEmail);
      setEmail(newEmail);
      setMessageEmail('Adresse email modifiée avec succès.');
      setNewEmail('');
      setEmailPassword('');
    } catch (err) {
      if (err.code === 'auth/wrong-password') {
        setErrorEmail('Mot de passe incorrect.');
      } else if (err.code === 'auth/invalid-email') {
        setErrorEmail('Adresse email invalide.');
      } else if (err.code === 'auth/email-already-in-use') {
        setErrorEmail('Cette adresse email est déjà utilisée.');
      } else {
        setErrorEmail('Erreur lors du changement d\'email. Veuillez vérifier vos informations.');
      }
      console.error('Erreur lors du changement d\'email :', err);
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!childName.trim() || !childFirstName.trim() || !childClass.trim()) {
      setError("Veuillez remplir tous les champs.");
      return;
    }

    try {
      const user = auth.currentUser;
      const childrenRef = collection(db, 'users', user.uid, 'children');

      if (isEditing && selectedChild) {
        // Mettre à jour l'enfant existant
        const childDocRef = doc(childrenRef, selectedChild);
        await updateDoc(childDocRef, {
          name: childName.toUpperCase(),
          firstName: childFirstName,
          class: childClass, // Enregistrer la classe sélectionnée
        });
        setMessage('Enfant modifié avec succès.');
      } else {
        // Ajouter un nouvel enfant
        await addDoc(childrenRef, {
          name: childName.toUpperCase(),
          firstName: childFirstName,
          class: childClass, // Enregistrer la classe sélectionnée
        });
        setMessage('Enfant ajouté avec succès. Vous pouvez maintenant ');
      }

      // Mettre à jour la liste des enfants après l'ajout ou la modification
      const childrenSnap = await getDocs(childrenRef);
      const childrenList = childrenSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setChildren(childrenList);

      // Réinitialiser les champs
      setChildName('');
      setChildFirstName('');
      setChildClass(''); // Réinitialiser la classe
      setIsEditing(false);
      setSelectedChild('');
    } catch (err) {
      setError('Erreur lors de l\'ajout ou de la modification de l\'enfant.');
      console.error('Erreur lors de l\'ajout ou de la modification de l\'enfant :', err);
    }
  };

  const handleEditChild = (child) => {
    setSelectedChild(child.id);
    setChildName(child.name);
    setChildFirstName(child.firstName);
    setChildClass(child.class); // Charger la classe existante
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    // Réinitialiser les champs du formulaire et l'état d'édition
    setChildName('');
    setChildFirstName('');
    setChildClass('');
    setIsEditing(false);
    setSelectedChild('');
    setMessage('');
    setError('');
  };

  const handleOpenDialog = (childId) => {
    setSelectedChild(childId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDeleteChild = async () => {
    if (!selectedChild) {
      setError('ID de l\'enfant invalide.');
      return;
    }

    try {
      const user = auth.currentUser;
      const childDocRef = doc(db, 'users', user.uid, 'children', selectedChild);
      await deleteDoc(childDocRef);

      // Mise à jour de la liste des enfants après la suppression
      setChildren((prevChildren) => prevChildren.filter(child => child.id !== selectedChild));
      handleCloseDialog(); // Fermer le dialogue après suppression
      setSelectedChild(''); // Réinitialiser l'ID de l'enfant sélectionné
      setMessage('Enfant supprimé avec succès.');
    } catch (err) {
      setError('Erreur lors de la suppression de l\'enfant.');
      console.error('Erreur lors de la suppression de l\'enfant :', err);
    }
  };

  return (
    <div>
      <Paper sx={{ padding: 1, marginTop: 1 }}>
        <Typography variant="h4" gutterBottom>
          Mon Profil
        </Typography>
        <Box mt={3}>
          <Typography variant="h6">Email : {email}</Typography>
        </Box>

        {/* Affichage de la liste des enfants */}
        <Box mt={3}>
          <Typography variant="h6">Mes Enfants</Typography>
          {children.length > 0 ? (
            children.map((child) => (
              <Box
                key={child.id}
                sx={{
                  marginBottom: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid #ccc', // Ajouter une bordure horizontale
                  paddingBottom: 1 // Ajouter un espace entre la bordure et le contenu
                }}
              >
                <Typography>
                  {child.name} {child.firstName} - {child.class} {/* Afficher la classe */}
                </Typography>
                <Box>
                  {isMobile ? (
                    <>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditChild(child)}
                        sx={{ marginRight: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDialog(child.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleEditChild(child)}
                        sx={{ marginRight: 1, textTransform: 'none' }}
                      >
                        <EditIcon />Modifier
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleOpenDialog(child.id)}
                        sx={{ textTransform: 'none' }}
                      >
                        <DeleteIcon />Supprimer
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Typography>Aucun enfant ajouté.</Typography>
          )}
        </Box>

        {/* Formulaire d'ajout ou de modification d'un enfant */}
        <Box mt={5}>
          <Typography variant="h6">{isEditing ? 'Modifier l\'enfant' : 'Ajouter un enfant'}</Typography>
          {message && <Alert severity="success">{message}<Link to="/" >
                  l'inscrire à la cantine...
                </Link></Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          <form onSubmit={handleAddChild}>
            <TextField
              label="Nom de l'enfant"
              variant="outlined"
              fullWidth
              margin="normal"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
            />
            <TextField
              label="Prénom de l'enfant"
              variant="outlined"
              fullWidth
              margin="normal"
              value={childFirstName}
              onChange={(e) => setChildFirstName(e.target.value)}
            />

            {/* Liste déroulante pour sélectionner la classe */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Classe</InputLabel>
              <Select
                value={childClass}
                onChange={(e) => setChildClass(e.target.value)}
                label="Classe"
              >
                <MenuItem value="PS/MS">PS/MS</MenuItem>
                <MenuItem value="GS/CP">GS/CP</MenuItem>
                <MenuItem value="CE1/CE2">CE1/CE2</MenuItem>
                <MenuItem value="CE2/CM1">CE2/CM1</MenuItem>
                <MenuItem value="CM1CM2">CM1/CM2</MenuItem>
                {/* Ajouter d'autres classes si nécessaire */}
              </Select>
            </FormControl>

            <Box sx={{
              marginBottom: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'end',
            }} mt={2}>

              {isEditing && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCancelEdit}
                  sx={{ marginLeft: 2 }}
                >
                  Annuler
                </Button>
              )}

              <Button variant="contained" color="success" type="submit">
                {isEditing ? 'Mettre à jour' : 'Ajouter l\'enfant'}
              </Button>
            </Box>
          </form>
        </Box>

        {/* Formulaire de modification de l'email */}
        <Box mt={5}>
          <Typography variant="h6">Modifier l'adresse email</Typography>
          {messageEmail && <Alert severity="success">{messageEmail}</Alert>}
          {errorEmail && <Alert severity="error">{errorEmail}</Alert>}
          <TextField
            label="Nouvelle adresse email"
            variant="outlined"
            type="email"
            fullWidth
            margin="normal"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
          <TextField
            label="Mot de passe actuel"
            variant="outlined"
            type="password"
            fullWidth
            margin="normal"
            value={emailPassword}
            onChange={(e) => setEmailPassword(e.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleChangeEmail}
            sx={{ marginTop: 2 }}
          >
            Modifier l'email
          </Button>
        </Box>

        {/* Formulaire de modification du mot de passe */}
        <Box mt={5}>
          <Typography variant="h6">Modifier le mot de passe</Typography>
          {messageMdp && <Alert severity="success">{messageMdp}</Alert>}
          {errorMdp && <Alert severity="error">{errorMdp}</Alert>}
          <TextField
            label="Mot de passe actuel"
            variant="outlined"
            type="password"
            fullWidth
            margin="normal"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <TextField
            label="Nouveau mot de passe"
            variant="outlined"
            type="password"
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <TextField
            label="Confirmer le nouveau mot de passe"
            variant="outlined"
            type="password"
            fullWidth
            margin="normal"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleChangePassword}
            sx={{ marginTop: 2 }}
          >
            Mettre à jour le mot de passe
          </Button>
        </Box>
      </Paper>
      <ConfirmationDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onConfirm={handleDeleteChild}
        title="Confirmation"
        message="Êtes-vous sûr de vouloir supprimer cet enfant ? Cette action est irréversible."
      />
    </div>
  );
};

export default ProfilePage;
